import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  MenuItem,
  CircularProgress,
  Box,
} from '@mui/material';
import { Feedback as FeedbackIcon } from '@mui/icons-material';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { useSnackbar } from '../Snackbar/SnackbarProvider';
import { supportService } from '../../services/supportService';
import { SupportIssueRequest } from '../../types';

interface FeedbackDialogProps {
  open: boolean;
  onClose: () => void;
}

const categoryOptions = [
  { value: 'bug', label: '🐛 Fehler melden' },
  { value: 'feature', label: '💡 Verbesserung vorschlagen' },
  { value: 'question', label: '❓ Frage stellen' },
] as const;

const validationSchema = Yup.object({
  title: Yup.string().required('Titel ist erforderlich').max(200, 'Maximal 200 Zeichen'),
  description: Yup.string().required('Beschreibung ist erforderlich'),
  category: Yup.string()
    .oneOf(['bug', 'feature', 'question'])
    .required('Kategorie ist erforderlich'),
});

/**
 * Dialog zum Senden von Feedback, Fehlerberichten oder Fragen.
 * Nutzt Formik + Yup für Validierung und supportService für die API-Kommunikation.
 */
const FeedbackDialog: React.FC<FeedbackDialogProps> = ({ open, onClose }) => {
  const [loading, setLoading] = useState(false);
  const { showSnackbar } = useSnackbar();

  const formik = useFormik({
    initialValues: {
      title: '',
      description: '',
      category: 'bug' as 'bug' | 'feature' | 'question',
    },
    validationSchema,
    onSubmit: async (values) => {
      setLoading(true);
      try {
        const requestData: SupportIssueRequest = {
          title: values.title,
          description: values.description,
          category: values.category,
          page_url: window.location.href,
          browser: navigator.userAgent,
        };
        const response = await supportService.createIssue(requestData);
        showSnackbar(
          `Feedback wurde erfolgreich gesendet (Issue #${response.issue_number})`,
          'success'
        );
        formik.resetForm();
        onClose();
      } catch {
        showSnackbar('Feedback konnte nicht gesendet werden. Bitte versuche es erneut.', 'error');
      } finally {
        setLoading(false);
      }
    },
  });

  const handleClose = () => {
    if (!loading) {
      formik.resetForm();
      onClose();
    }
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <FeedbackIcon color="primary" />
        Feedback / Problem melden
      </DialogTitle>
      <form onSubmit={formik.handleSubmit}>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
            <TextField
              select
              id="category"
              name="category"
              label="Kategorie"
              value={formik.values.category}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              error={formik.touched.category && Boolean(formik.errors.category)}
              helperText={formik.touched.category && formik.errors.category}
              fullWidth
              size="small"
            >
              {categoryOptions.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </TextField>

            <TextField
              id="title"
              name="title"
              label="Titel"
              value={formik.values.title}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              error={formik.touched.title && Boolean(formik.errors.title)}
              helperText={formik.touched.title && formik.errors.title}
              fullWidth
              size="small"
              slotProps={{ htmlInput: { maxLength: 200 } }}
            />

            <TextField
              id="description"
              name="description"
              label="Beschreibung"
              value={formik.values.description}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              error={formik.touched.description && Boolean(formik.errors.description)}
              helperText={formik.touched.description && formik.errors.description}
              fullWidth
              multiline
              rows={4}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose} disabled={loading}>
            Abbrechen
          </Button>
          <Button
            type="submit"
            variant="contained"
            disabled={loading}
            startIcon={loading ? <CircularProgress size={16} /> : undefined}
          >
            Absenden
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
};

export default FeedbackDialog;
